import type { PillTone } from '@/components/cccd/StatusPill';

/**
 * ISO (UTC) -> valor de un <input type="datetime-local"> en HORA LOCAL.
 * Usar `toISOString().slice(0, 16)` da la hora UTC (5 h adelante de Colombia): al guardar, el
 * input se interpreta como local y cada guardado corría las fechas +5 h.
 */
export function toDateTimeLocal(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

/**
 * Orden de fechas de un evento (mismas dos reglas que aplica el API). Devuelve el mensaje en
 * español o null si todo está en orden; los valores son los de los <input type="datetime-local">.
 */
export function validateEventDates(dates: {
  eventStartDate: string;
  eventEndDate: string;
  registrationStartDate: string;
  registrationDeadline: string;
}): string | null {
  const t = (v: string) => (v ? new Date(v).getTime() : NaN);
  if (t(dates.eventEndDate) <= t(dates.eventStartDate)) {
    return 'La fecha de cierre del concurso debe ser posterior a la fecha de inicio.';
  }
  if (t(dates.registrationDeadline) <= t(dates.registrationStartDate)) {
    return 'El cierre de inscripciones debe ser posterior a la apertura de inscripciones.';
  }
  return null;
}

// Small computed status pills shown next to a date-range Section header
// (e.g. "Dura 3 días", "Cierran en 4 días") — cccd-ux-redesign design canvas.
export function durationPill(startIso: string, endIso: string): { label: string; tone: PillTone } | null {
  if (!startIso || !endIso) return null;
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const days = Math.max(1, Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1);
  return { label: days === 1 ? '1 día' : `Dura ${days} días`, tone: 'brand' };
}

export function registrationWindowPill(startIso: string, deadlineIso: string): { label: string; tone: PillTone } | null {
  if (!startIso || !deadlineIso) return null;
  const start = new Date(startIso);
  const deadline = new Date(deadlineIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(deadline.getTime())) return null;
  const now = new Date();
  if (now < start) return { label: 'Aún no abren', tone: 'gray' };
  if (now > deadline) return { label: 'Cerradas', tone: 'gray' };
  const daysLeft = Math.max(0, Math.ceil((deadline.getTime() - now.getTime()) / 86_400_000));
  if (daysLeft <= 7) return { label: daysLeft === 0 ? 'Cierran hoy' : `Cierran en ${daysLeft} días`, tone: 'warning' };
  return { label: `Abiertas ${daysLeft} días`, tone: 'success' };
}
